import * as React from 'react';

type ScrollBehaviorLike = 'auto' | 'instant' | 'smooth';
type ScrollLogicalPositionLike = 'start' | 'center' | 'end' | 'nearest';

type Options = {
  containerRef: React.RefObject<HTMLElement>;
  deps: React.DependencyList;
  threshold?: number;
  scrollOnMount?: boolean;
  behavior?: ScrollBehaviorLike;
  block?: ScrollLogicalPositionLike;
};

export function useStickyAutoScroll({ containerRef, deps, threshold = 80, scrollOnMount = false }: Options) {
  const bottomRef = React.useRef<HTMLDivElement | null>(null);
  const isFirstRender = React.useRef(true);

  const stickToBottomRef = React.useRef(true);
  const [isNearBottom, setIsNearBottom] = React.useState(true);

  React.useEffect(() => {
    const el = containerRef.current;

    if (!el) {
      return;
    }

    const computeNearBottom = () => {
      const distance = el.scrollHeight - (el.scrollTop + el.clientHeight);
      const near = distance <= threshold;

      setIsNearBottom(near);
      stickToBottomRef.current = near;
    };

    computeNearBottom();
    const onScroll = () => computeNearBottom();

    el.addEventListener('scroll', onScroll, { passive: true });

    return () => el.removeEventListener('scroll', onScroll);
  }, [containerRef, threshold]);

  const scrollContainerToBottom = React.useCallback(() => {
    const el = containerRef.current;

    if (!el) {
      return;
    }

    requestAnimationFrame(() => {
      el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' });
    });
  }, [containerRef]);

  React.useEffect(() => {
    const first = isFirstRender.current;
    const shouldScroll = first ? scrollOnMount : stickToBottomRef.current;

    if (shouldScroll) {
      scrollContainerToBottom();
    }

    isFirstRender.current = false;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  React.useEffect(() => {
    const el = containerRef.current;

    if (!el) {
      return;
    }

    const observer = new MutationObserver(() => {
      if (stickToBottomRef.current) {
        scrollContainerToBottom();
      }
    });

    observer.observe(el, { childList: true, subtree: true, characterData: true });

    return () => observer.disconnect();
  }, [containerRef, scrollContainerToBottom]);

  const scrollNow = React.useCallback(() => {
    stickToBottomRef.current = true;
    scrollContainerToBottom();
  }, [scrollContainerToBottom]);

  return { bottomRef, isNearBottom, scrollNow };
}
